import { Box, Stack } from '@mui/material';
import { useState } from 'react';
import { useNavigate } from 'react-router';

import { createAndSendExternalInvitation } from '@/domain/external-invitation/actions/create-and-send-external-invitation.action';
import { selectExternalInvitationsForScopes } from '@/domain/external-invitation/external-invitations.selectors';
import { getCategories } from '@/domain/external-invitation/utils/category/get-categories';
import { getScopeFromModeSectorAndCategory } from '@/domain/external-invitation/utils/scopes/get-scope';
import { getScopesFromModeAndSector } from '@/domain/external-invitation/utils/scopes/get-scopes';
import { ScoreType } from '@/domain/impact/impact.entity';
import {
  selectImpactCalculateFestivalCo2ForProject,
  selectImpactCalculateFestivalEnvForProject,
  selectImpactCalculateFestivalEssForProject,
} from '@/domain/impact-calculate-festival/impact-calculate-festival.selectors';
import { selectImpactReferences } from '@/domain/impact-references/references.selectors';
import {
  selectCurrentProjectFestival,
  selectIsFetchingCurrentProjectFestival,
  selectProjectFestivalCurrentSector,
} from '@/domain/project-festival/project-festival.selectors';
import { LoadingView } from '@/ui/components/LoadingView';
import { PageTitle } from '@/ui/components/PageTitle';
import { useAppDispatch, useAppSelector } from '@/ui/hook/store';
import { translate } from '@/ui/i18n';
import { ImpactQuestionContext } from '@/ui/pages/app/components/quiz-panel/ImpactQuestionContext';
import { QuizPanel } from '@/ui/pages/app/components/quiz-panel/QuizPanel';
import { prepareQuizPanelDescription } from '@/ui/pages/app/components/quiz-panel/utils/prepare-quiz-panel-description';
import { InviteSubmitData } from '@/ui/pages/app/components/stakeholders/assign-external/components/FormExternalInvite';
import { TopicScoreDetailsData } from '@/ui/pages/app/components/topic-score-card/type';
import { SectorMainContent } from '@/ui/pages/app/project/festival/project-sector/components/SectorMainContent';
import { prepareScoreSectorDetailsDataCo2 } from '@/ui/pages/app/project/festival/project-sector/utils/prepare-score-sector-details-data/co2';
import { prepareScoreSectorDetailsDataEnv } from '@/ui/pages/app/project/festival/project-sector/utils/prepare-score-sector-details-data/env';
import { prepareScoreSectorDetailsDataEss } from '@/ui/pages/app/project/festival/project-sector/utils/prepare-score-sector-details-data/ess';

import { getFestivalCo2ExternalQuizFormForSector } from '../utils/festival-co2-external-quiz-form-data';
import { getFestivalCo2QuizFormForSector } from '../utils/festival-co2-quiz-form-data';
import { envFestivalQuizLayout } from '../utils/festival-env-quiz-layout';
import { essFestivalQuizLayout } from '../utils/festival-ess-quiz-layout';

export function ProjectSector() {
  const navigate = useNavigate();

  const dispatch = useAppDispatch();

  const sector = useAppSelector(selectProjectFestivalCurrentSector);

  const project = useAppSelector(selectCurrentProjectFestival);
  const isFetchingCurrentProject = useAppSelector(
    selectIsFetchingCurrentProjectFestival,
  );

  const calculateCo2 = useAppSelector(
    selectImpactCalculateFestivalCo2ForProject(project?.id || -1),
  );
  const calculateEnv = useAppSelector(
    selectImpactCalculateFestivalEnvForProject(project?.id || -1),
  );
  const calculateEss = useAppSelector(
    selectImpactCalculateFestivalEssForProject(project?.id || -1),
  );
  const references = useAppSelector(selectImpactReferences);

  const allData: {
    co2: TopicScoreDetailsData;
    env: TopicScoreDetailsData;
    ess: TopicScoreDetailsData;
  } = {
    co2: prepareScoreSectorDetailsDataCo2(calculateCo2, references, sector),
    env: prepareScoreSectorDetailsDataEnv(calculateEnv, sector || ''),
    ess: prepareScoreSectorDetailsDataEss(calculateEss, sector || ''),
  };

  const [panelOpen, setPanelOpen] = useState(false);
  const [hideCompleted, setHideCompleted] = useState(false);

  const [selectedScoreType, setSelectedScoreType] = useState<ScoreType>('co2');
  const [selectedDetailsValueId, setSelectedDetailsValueId] =
    useState<string>('');

  const onClickBack = () => {
    if (project) navigate('/app/projects/festival/' + project.id);
  };

  const onSubmitInvite = async (data: InviteSubmitData) => {
    const scope = getScopeFromModeSectorAndCategory(
      'festival',
      data.sector,
      data.category,
    );
    const params = {
      name: data.name,
      email: data.email,
      limitDate: data.limitDate,
      eventPosition: undefined,
      title: '',
      projectId: project?.id || -1,
      sector: data.sector,
      scope: scope,
    };
    await dispatch(createAndSendExternalInvitation(params));
  };

  const externalCategories = getCategories('festival', sector || '');
  const externalScopes = getScopesFromModeAndSector('festival', sector || '');
  const externalExistingInvitations = useAppSelector(
    selectExternalInvitationsForScopes(externalScopes),
  );

  const quizPanelDescription = prepareQuizPanelDescription(
    selectedScoreType,
    sector || '',
  );

  const enableExternalPublic = sector === 'PUBLIC';

  return (
    <Box>
      <ImpactQuestionContext.Provider
        value={{
          panelOpen,
          hideCompleted,
          setHideCompleted,
          setPanelOpen,
        }}
      >
        <Stack width={'calc(100% - 21rem)'}>
          <PageTitle
            title={sector ? translate(`Sector.${sector}-sector`) : ''}
            subtitle={sector ? translate(`Sector.${sector}-description`) : ''}
            backButtonText={project?.name || ''}
            onClickBack={onClickBack}
          />
          {isFetchingCurrentProject && <LoadingView />}
          {!isFetchingCurrentProject && project && sector && (
            <Box sx={{ mt: 6 }}>
              <SectorMainContent
                allData={allData}
                setSelectedScoreType={setSelectedScoreType}
                setSelectedDetailsValueId={setSelectedDetailsValueId}
              />
            </Box>
          )}
        </Stack>
        {sector && (
          <QuizPanel
            projectId={project?.id || -1}
            quizScoreType={selectedScoreType}
            quizSubType={selectedDetailsValueId}
            sector={sector}
            externalCategories={externalCategories}
            externalExistingInvitations={externalExistingInvitations}
            onSubmitInvite={onSubmitInvite}
            co2FormData={getFestivalCo2QuizFormForSector(sector)}
            co2ExternalFormData={getFestivalCo2ExternalQuizFormForSector(
              sector,
            )}
            filterBySector
            description={quizPanelDescription.description}
            descriptionTootltip={quizPanelDescription.descriptionTootltip}
            essQuizLayout={essFestivalQuizLayout}
            envQuizLayout={envFestivalQuizLayout}
            enableExternalPublic={enableExternalPublic}
            enableExternalAnswsersView={true}
            enableImportedAnswersView={true}
          />
        )}
      </ImpactQuestionContext.Provider>
    </Box>
  );
}
